import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Select } from 'antd'
import { computeSelectedWeekForSeason, getWeekFromWeek, getYearFromWeek, SeasonRange, seasonReferenceYear, toAbsoluteUrl } from '../../../../../../_metronic/helpers'
import Plot from 'react-plotly.js'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useSelectedWeek } from '../../../../../contexts/SelectedWeekContext'
import SourceLink from '../../component/chartLinkData'
const { Option } = Select

interface DataItem {
  week: string
  positivePerceptionCovid: number | null
  negativePerceptionCovid: number | null
  neutrePerceptionCovid: number | null
  positivePerceptionVrs: number | null
  negativePerceptionVrs: number | null
  neutrePerceptionVrs: number | null
  positivePerceptionGrippe: number | null
  negativePerceptionGrippe: number | null
  neutrePerceptionGrippe: number | null
}
interface SelectedDataItem {
  week: string
  positivePerception: number | null
  negativePerception: number | null
  neutrePerception: number | null
  totalMessages: number | null
}

type PerceptionsOnVaccinationProps = {
  season: SeasonRange,
  onClick?: () => void
  selectedVirus: 'Influenza' | 'Covid' | 'RSV'
  onVirusChange: (value: 'Influenza' | 'Covid' | 'RSV') => void
}
const PerceptionsOnVaccination: React.FC<PerceptionsOnVaccinationProps> = ({ season, onClick, selectedVirus, onVirusChange }) => {
  const [data, setData] = useState<DataItem[]>([])
  const [missingData, setMissingData] = useState<DataItem[]>([])
  const [selectedData, setSelectedData] = useState<SelectedDataItem[]>([])
  const { selectedWeek, setSelectedWeek } = useSelectedWeek('2025-W40')
  const [selectedVirusMap, setSelectedVirusMap] = useState('Influenza')
  const [showArrow, setShowArrow] = useState<boolean>(false)
  const API_URL = process.env.REACT_APP_API_URL

  const [seasonSelectedWeek, setSeasonSelectedWeek] = useState<string | null>(null)
  const startYear = getYearFromWeek(season.startWeek)
  const endYear = getYearFromWeek(season.endWeek)
  const startWeekNumber = getWeekFromWeek(season.startWeek) + 1

  function addMissingWeek(data: DataItem[]): DataItem[] {
    const startWeek = season.startWeek // First week in the data
    // const endWeek = data[data.length - 1].week // Last week in the data
    const endWeek = season.endWeek
    const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
    const newData: DataItem[] = []
    const missingData: DataItem[] = []
    let currentWeek = startWeek

    while (currentWeek !== endWeek) {
      // Move to the next week
      const [year, weekNum] = currentWeek.split('-W')
      const weekNumber = parseInt(weekNum)
      if (weekNumber < 52) {
        currentWeek = `${year}-W${(weekNumber + 1).toString()}`
      } else {
        currentWeek = `${parseInt(year) + 1}-W1`
      }
      if (!existingWeeks.has(currentWeek)) {
        newData.push({
          week: currentWeek,
          positivePerceptionCovid: null,
          negativePerceptionCovid: null,
          neutrePerceptionCovid: null,
          positivePerceptionVrs: null,
          negativePerceptionVrs: null,
          neutrePerceptionVrs: null,
          positivePerceptionGrippe: null,
          negativePerceptionGrippe: null,
          neutrePerceptionGrippe: null,
        })
        missingData.push({
          week: currentWeek,
          positivePerceptionCovid: null,
          negativePerceptionCovid: null,
          neutrePerceptionCovid: null,
          positivePerceptionVrs: null,
          negativePerceptionVrs: null,
          neutrePerceptionVrs: null,
          positivePerceptionGrippe: null,
          negativePerceptionGrippe: null,
          neutrePerceptionGrippe: null,
        })
      }
    }

    // Find the index to insert the new week
    let insertionIndex = 0
    for (let i = 0; i < data.length; i++) {
      if (data[i].week < `${startYear}-W52`) {
        insertionIndex = i + 1
      } else {
        break
      }
    }
    setMissingData(missingData)
    // Merge existing data with the newly added missing weeks
    const mergedData = [
      ...data.slice(0, insertionIndex),
      ...newData,
      ...data.slice(insertionIndex),
    ].sort((a, b) => {
      const aYear = parseInt(a.week.substring(0, 4), 10)
      const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
      const bYear = parseInt(b.week.substring(0, 4), 10)
      const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
      if (aYear !== bYear) {
        return aYear - bYear
      } else {
        return aWeek - bWeek
      }
    })

    return mergedData
  }
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(API_URL + '/vaccination_perceptions_winter2324s')
        // setData(addMissingWeek(response.data))
        setData(
          addMissingWeek(response.data).filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );
          })
        )

        let endWeek = season?.endWeek?.split('-W')
        const alignedWeek =
          startYear == seasonReferenceYear
            ? selectedWeek
            : computeSelectedWeekForSeason(selectedWeek, season.startWeek)
        const selectWeek = alignedWeek.split('-W')
        const showArrow =
          (Number(selectWeek[0]) > startYear ||
            (Number(selectWeek[0]) === startYear && Number(selectWeek[1]) >= startWeekNumber)) &&
          (Number(selectWeek[0]) < Number(endWeek[0]) ||
            (Number(selectWeek[0]) === Number(endWeek[0]) &&
              Number(selectWeek[1]) <= Number(endWeek[1])))
        setShowArrow(showArrow)
        setSeasonSelectedWeek(alignedWeek);

        const selectInfluenza = addMissingWeek(response.data).filter((entry) => {
          const currentYear = parseInt(entry.week.substring(0, 4), 10)
          const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
          return (
            (currentYear === startYear && currentWeek >= startWeekNumber) ||
            (currentYear === endYear && currentWeek <= 27) ||
            (currentYear > startYear && currentYear < endYear)
          );
        })
          .map((item) => ({
            week: item.week,
            positivePerception: item.positivePerceptionGrippe,
            negativePerception: item.negativePerceptionGrippe,
            neutrePerception: item.neutrePerceptionGrippe,
            totalMessages:
              (item.positivePerceptionGrippe ?? 0) +
              (item.negativePerceptionGrippe ?? 0) +
              (item.neutrePerceptionGrippe ?? 0),
          }))

        setSelectedData(selectInfluenza)
        // Do something with selectCovid, like setting it to another state variable if needed
        // For example: setSelectCovidData(selectCovid);
      } catch (error) {
        console.error('Error fetching data:', error)
        // Handle error or set a default value for data
        setData([])
      }
    }


    fetchData()
  }, [selectedWeek])

  useEffect(() => {
    if (selectedVirus && data.length) {
      handleDataChange(selectedVirus)
    }
  }, [selectedVirus, data])

  function handleDataChange(value) {
    onVirusChange(value)
    if (value === 'Covid') {
      const selectCovid = data.map((item) => ({
        week: item.week,
        positivePerception: item.positivePerceptionCovid,
        negativePerception: item.negativePerceptionCovid,
        neutrePerception: item.neutrePerceptionCovid,
        totalMessages:
          (item.positivePerceptionCovid ?? 0) + (item.negativePerceptionCovid ?? 0) + (item.neutrePerceptionCovid ?? 0),
      }))
      setSelectedData(selectCovid)
      setSelectedVirusMap('SARS-CoV2')
    } else if (value === 'Influenza') {
      const selectInfluenza = data.map((item) => ({
        week: item.week,
        positivePerception: item.positivePerceptionGrippe,
        negativePerception: item.negativePerceptionGrippe,
        neutrePerception: item.neutrePerceptionGrippe,
        totalMessages:
          (item.positivePerceptionGrippe ?? 0) +
          (item.negativePerceptionGrippe ?? 0) +
          (item.neutrePerceptionGrippe ?? 0),
      }))
      setSelectedData(selectInfluenza)
      setSelectedVirusMap('Influenza')
    } else {
      const selectRSV = data.map((item) => ({
        week: item.week,
        positivePerception: item.positivePerceptionVrs,
        negativePerception: item.negativePerceptionVrs,
        neutrePerception: item.neutrePerceptionVrs,
        totalMessages:
          (item.positivePerceptionVrs ?? 0) + (item.negativePerceptionVrs ?? 0) + (item.neutrePerceptionVrs ?? 0),
      }))
      setSelectedData(selectRSV)
      setSelectedVirusMap('RSV')
    }
  }

  return (
    <>
      <div className='card px-4'>
        <div className='card-body row justify-content-md-center'>
          {
            startYear == seasonReferenceYear &&
            <h2 className='text-primary p-2  bold'>
              Perceptions on vaccination campaigns Winter 2025-2026 (France){' '}
              <OverlayTrigger
                placement='right'
                overlay={
                  <Tooltip id='logo-tooltip' style={{ width: '300px' }}>
                    <p>
                      Weekly evolution of acceptation of vaccination campaigns for Influenza,
                      SARS-Cov2 and RSV based on social media analysis: For each message extracted, a
                      pre-trained sentiment model is applied, with an output that can be positive,
                      negative or neutral. A message is attributed the “positive” label if it
                      characterizes the policies as efficient or shows that they are accepted by the
                      writer of the message. On the other hand, a message is labeled “negative” when
                      the message describes these policies as inefficient or having a significative
                      negative impact on the person’s quality of life, or generally if the person
                      doesn’t adhere to the policies. Source: KapCode
                    </p>
                  </Tooltip>
                }
              >
                <img
                  //className='mb-1'
                  style={{ width: '12px' }}
                  alt='logo'
                  src={toAbsoluteUrl('/media/logos/ii.svg')}
                />
              </OverlayTrigger>
            </h2>
          }
          <div className='card' style={{ height: '500px' }}>
            <div className='row'>
              <label style={{ width: '10%' }} className='col-1 mt-3 text-primary fw-medium fs-5'>
                Virus :
              </label>
              <div className='col-6'>
                <Select
                  style={{ width: '70%' }}
                  size='large'
                  defaultValue='Influenza'
                  onChange={handleDataChange}
                  value={selectedVirus}
                >
                  <Option key={'Influenza'} value={'Influenza'}>
                    Influenza
                  </Option>
                  <Option key={'Covid'} value={'Covid'} selected>
                    SARS-CoV2
                  </Option>

                  <Option key={'RSV'} value={'RSV'}>
                    RSV
                  </Option>
                </Select>
              </div>
            </div>
            {data.length > 0 ? (
              <Plot
                data={[
                  {
                    type: 'bar',
                    marker: { color: '#e02828' },
                    name: 'Negative perception',
                    x: data.map((entry) => entry.week),
                    y: selectedData.map(
                      (entry) => ((entry.negativePerception ?? 0) * 100) / (entry.totalMessages ?? 0)
                    ),
                    text: selectedData.map((entry) => entry.negativePerception).map(String),
                    hovertemplate:
                      '<b>%{fullData.name}</b><br>' +
                      'Week: %{x}<br>' +
                      '%{fullData.name}: %{y}<extra></extra>',
                  },
                  {
                    type: 'bar',

                    marker: { color: '#ed7d31' },
                    name: 'Neutral perception',
                    x: data.map((entry) => entry.week),
                    y: selectedData.map(
                      (entry) => ((entry.neutrePerception ?? 0) * 100) / (entry.totalMessages ?? 0)
                    ),
                    text: selectedData.map((entry) => entry.neutrePerception).map(String),
                    hovertemplate:
                      '<b>%{fullData.name}</b><br>' +
                      'Week: %{x}<br>' +
                      '%{fullData.name}: %{y}<extra></extra>',
                  },
                  {
                    type: 'bar',
                    marker: { color: '#70ad47' },
                    name: 'Positive perception',
                    x: data.map((entry) => entry.week),
                    y: selectedData.map(
                      (entry) => ((entry.positivePerception ?? 0) * 100) / (entry.totalMessages ?? 0)
                    ),
                    text: selectedData.map((entry) => entry.positivePerception).map(String),
                    hovertemplate:
                      '<b>%{fullData.name}</b><br>' +
                      'Week: %{x}<br>' +
                      '%{fullData.name}: %{y}<extra></extra>',
                  },

                  {
                    type: 'scatter',
                    mode: 'lines',
                    yaxis: 'y2',
                    marker: { color: '#203879' },
                    name: 'Number of messages reported',
                    // On filtre les semaines qui ne sont pas dans missingData
                    x: selectedData
                      .filter(entry => !missingData.some(m => m.week === entry.week))
                      .map(entry => entry.week),
                    y: selectedData
                      .filter(entry => !missingData.some(m => m.week === entry.week))
                      .map(entry => entry.totalMessages),
                    connectgaps: false,
                    hovertemplate:
                      '<b>%{fullData.name}</b><br>' +
                      'Week: %{x}<br>' +
                      'Number of messages: %{y}<extra></extra>',
                  },
                  {
                    type: 'scatter',
                    mode: 'markers',
                    yaxis: 'y2',
                    name: 'Missing Data',
                    x: missingData.map((entry) => entry.week),
                    y: missingData.map((entry) => 0),
                    marker: { color: '#000000ff' },
                    hovertemplate:
                      '<b>%{fullData.name}</b><br>' +
                      'Week: %{x}<br>' +
                      '%{fullData.name}: %{y}<extra></extra>',
                  },
                ]}
                layout={{
                  annotations: [
                    {
                      xref: 'paper',
                      yref: 'paper',
                      x: -0.02,
                      xanchor: 'right',
                      y: 0.5,
                      yanchor: 'bottom',
                      text: '%',
                      font: {
                        size: 25,

                        //color: '#BBBBBC',
                      },
                      showarrow: false,
                    },
                    {
                      xref: 'paper',
                      yref: 'paper',
                      x: 1.03,
                      xanchor: 'left',
                      y: 0.4,
                      yanchor: 'bottom',
                      text: '#',
                      font: {
                        size: 25,

                        //color: '#1F77B4',
                      },
                      showarrow: false,
                    },
                    {
                      x: startYear == 2025
                        ? selectedWeek : seasonSelectedWeek,

                      xref: 'x',
                      yref: 'y',
                      text: showArrow ? 'Selected Week' : '',
                      font: {
                        family: 'Courier New, monospace',
                        size: 16,
                        color: '#CC1B1B',
                      },
                      arrowcolor: '#CC1B1B',
                      showarrow: showArrow,
                      arrowhead: 7,
                      ax: 0,
                      ay: -230,
                    },
                  ],
                  barmode: 'stack',

                  title: `Distribution of perceptions on ${selectedVirusMap} vaccination campaign (positive/neutral/negative)`,
                  xaxis: { title: 'Week', tickangle: -45 },
                  yaxis: {
                    // title: {
                    //   text: '%',
                    //   font: {
                    //     size: 25,
                    //     //color: '#1F77B4',
                    //   },
                    // },
                  },

                  yaxis2: {
                    showgrid: false,
                    titlefont: { color: '#203879' },
                    tickfont: { color: '#203879' },
                    overlaying: 'y',
                    side: 'right',
                    zeroline: false,
                  },
                  legend: {
                    x: 0,
                    y: -0.4, // Adjust the value to position the legend as per your requirement
                    orientation: 'h', // 'h' for horizontal, 'v' for vertical
                  },
                }}
                config={{ displayModeBar: true, displaylogo: false, responsive: true }}
              />
            ) : (
              <p>Loading data...</p>
            )}
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <p className='card-text source'>
              <i>
                Source:
                <a className='fw-bold' href='https://www.kapcode.fr/'>
                  {' '}
                  KapCode
                </a>
              </i>
            </p>
            {
              startYear == seasonReferenceYear &&
              <SourceLink onClick={onClick} />
            }
          </div>
        </div>
      </div>
    </>
  )
}

export default PerceptionsOnVaccination
